import React, { useEffect, useState } from "react";
import { LuMail, LuUser, LuChevronDown, LuPhone } from "react-icons/lu";
import { db } from "../utils/firebase";
import { collection, getDocs, orderBy, query, deleteDoc, doc } from "firebase/firestore";

function MessagesTable({ onDelete }) {
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "contactMessages"), orderBy("createdAt", "desc")));
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    await deleteDoc(doc(db, "contactMessages", id));
    setMessages((prev) => prev.filter((m) => m.id !== id));
    onDelete?.();
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted rounded-xl h-20 border border-border" />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-16 text-center">
        <LuMail size={40} className="text-[var(--color-muted-foreground)] opacity-40 mx-auto mb-3" />
        <p className="text-[var(--color-muted-foreground)] text-sm font-medium">No messages yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {messages.map((msg) => (
        <div key={msg.id} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {/* Header row */}
          <div
            className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-muted transition-colors"
            onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-[#eef4ff] dark:bg-accent/10 flex items-center justify-center flex-shrink-0">
                <LuUser size={16} className="text-brand-navy dark:text-accent" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{msg.name || "Anonymous"}</p>
                <p className="text-xs text-[var(--color-muted-foreground)] truncate">{msg.email}</p>
              </div>
              {msg.phone && (
                <span className="hidden sm:inline text-xs text-[var(--color-muted-foreground)] bg-muted px-2.5 py-1 rounded-full flex-shrink-0">
                  {msg.phone}
                </span>
              )}
              <p className="hidden md:block text-sm text-[var(--color-muted-foreground)] truncate max-w-xs ml-2">
                {msg.message?.slice(0, 80)}{msg.message?.length > 80 ? "…" : ""}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              <span className="text-xs text-[var(--color-muted-foreground)]">{formatDate(msg.createdAt)}</span>
              <LuChevronDown size={16} className={`text-[var(--color-muted-foreground)] transition-transform ${expanded === msg.id ? "rotate-180" : ""}`} />
            </div>
          </div>

          {/* Expanded message */}
          {expanded === msg.id && (
            <div className="px-6 pb-5 border-t border-border">
              <div className="pt-4 space-y-3">
                <div className="flex gap-2 flex-wrap text-xs text-[var(--color-muted-foreground)]">
                  {msg.email && (
                    <a href={`mailto:${msg.email}`} className="flex items-center gap-1.5 bg-muted border border-border px-3 py-1.5 rounded-lg hover:border-brand-amber transition-colors">
                      <LuMail size={14} />
                      {msg.email}
                    </a>
                  )}
                  {msg.phone && (
                    <a href={`tel:${msg.phone}`} className="flex items-center gap-1.5 bg-muted border border-border px-3 py-1.5 rounded-lg hover:border-brand-amber transition-colors">
                      <LuPhone size={14} />
                      {msg.phone}
                    </a>
                  )}
                </div>
                <p className="text-sm text-foreground leading-relaxed bg-muted rounded-xl p-4 whitespace-pre-wrap">
                  {msg.message}
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => handleDelete(msg.id)}
                    className="text-xs text-red-400 hover:text-red-600 dark:hover:text-red-300 font-semibold transition-colors"
                  >
                    Delete message
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default MessagesTable;
